import Property, { PropertyAPIResponse } from "@/classes/property";
import { DATA_API_SERVICE } from "@/appConfig";
import { AppAbilityChecker } from "@/appAbility";
import { UserStore } from "@/store";
import API from "@/api/api";
import apolloClient from "@/api/charge";
import { gql } from "@apollo/client/core";

export function canViewPropertyCheck(property: Property) {
  if (AppAbilityChecker.canViewAsAdmin()) return true;
  if (UserStore.profile.property) {
    return UserStore.profile.property === property.id;
  }
  return true;
}

async function findAll() {
  const res = await apolloClient.query({
    query: gql`
      query Properties {
        properties {
          id
          oKey: organizationId
          name
          address1
          address2
          city
          state
          zip
          latitude
          longitude
          phone
          email
          manager: contact
          kwSoftLimit: wattsSoftLimit
          propertyType
          utility: utilityProvider
          note
          gatewayType
          carrierName
          hasEditablePenalty
        }
      }
    `
  });
  const properties = res.data?.properties.map(property =>
    Property.fromAPI({
      ...property
    })
  );
  return properties.filter(property => {
    return canViewPropertyCheck(property);
  });
}

async function findTopProperties(query?: Partial<{ start: Date; end: Date }>) {
  const response = await API.post(`${DATA_API_SERVICE}/property/top`, query);
  return response.data;
}

async function findPropertyById(id: string) {
  const res = await apolloClient.query({
    query: gql`
      query Property($filter: PropertyFilter) {
        property(filter: $filter) {
          id
          oKey: organizationId
          name
          address1
          address2
          city
          state
          zip
          latitude
          longitude
          phone
          email
          manager: contact
          kwSoftLimit: wattsSoftLimit
          propertyType
          utility: utilityProvider
          note
          gatewayType
          carrierName
          hasEditablePenalty
        }
      }
    `,
    variables: {
      filter: {
        id
      }
    }
  });
  const property = Property.fromAPI({
    ...res.data?.property
  });
  if (!canViewPropertyCheck(property)) throw new Error("Unauthorized. Cannot view property.");
  return property;
}

async function createProperty(property: Property) {
  const propertyResponse = await API.post<PropertyAPIResponse>(`${DATA_API_SERVICE}/property`, property.toJson());
  return propertyResponse.data;
}

async function updateProperty(property: Property) {
  return await API.put(`${DATA_API_SERVICE}/property/${property.id}`, property.toJson());
}

async function updatePropertyPenalty(property: Property) {
  return await API.put(`${DATA_API_SERVICE}/property/penalty/${property.id}`, property.toJson());
}

async function archiveProperty(propertyId: string) {
  return await API.delete(`${DATA_API_SERVICE}/property/${propertyId}`);
}

async function getPropertiesByOrgId(organizationId: string) {
  const propertiesResponse = await API.get<PropertyAPIResponse[]>(
    `${DATA_API_SERVICE}/property/property/organization/${organizationId}`
  );
  return propertiesResponse.data.map(property => Property.fromAPI(property));
}

const APIProperty = {
  createProperty,
  archiveProperty,
  findAll,
  findPropertyById,
  getPropertiesByOrgId,
  updateProperty,
  updatePropertyPenalty,
  findTopProperties
};

export default APIProperty;
